// Pilot profile store — local-first IDB + opt-in Supabase sync.
//
// Behavior:
//   - When signed out (or Supabase not configured), this is a normal
//     local store: writes hit IndexedDB, reads come from RAM after
//     hydrate().
//   - When the auth store flips to signed-in, we run an outbox-style
//     sync:
//       1. Push every local pilot the user has up to app.pilots
//          (owner_id = user.id, client_id = pilot.id). Upsert by
//          (owner_id, client_id) so re-pushes don't duplicate.
//       2. Pull authoritative server state and replace the local
//          list (server is the canonical store once you're logged
//          in). Local IDB stays as the offline cache.
//   - Every subsequent mutation (add / update / setDefault / remove)
//     writes IDB first, then pushes the changed row to Supabase
//     fire-and-forget. On failure we record a syncError but don't
//     block local progress; the next mutation retries the push.
//
// The public API surface (list/add/update/remove/setDefault/etc.) is
// unchanged so PilotsSection / LogbookPage / TrackLogsPage don't
// need to know whether sync is happening.

import { create } from 'zustand';
import { createKvList } from '../lib/kvList';
import { supabase } from '../lib/supabase';
import { useAuthStore } from './authStore';
import { newClientId } from '../lib/ids';

export type PilotCertificateLevel =
  | 'student'
  | 'sport'
  | 'recreational'
  | 'private'
  | 'commercial'
  | 'atp'
  | 'cfi'
  | 'cfii'
  | 'mei';

export type PilotMedicalClass = 'class-1' | 'class-2' | 'class-3' | 'basicmed' | 'sport';

export interface PilotProfile {
  id: string;
  isDefault: boolean;

  /** Display name. */
  name: string;
  /** Auto-derived initials (overrideable). */
  initials?: string;

  /** FAA certificate number. Not used for any external API. */
  certificateNumber?: string;
  certificateLevel?: PilotCertificateLevel;
  ratings?: Record<string, boolean>;
  endorsements?: Record<string, boolean>;

  medicalClass?: PilotMedicalClass;
  medicalExpiresOn?: string;     // ISO date YYYY-MM-DD
  flightReviewOn?: string;       // last completed
  ipcOn?: string;                // last IPC

  email?: string;
  phone?: string;
  notes?: string;

  createdAt: number;
  updatedAt: number;
}

const { loadAll, saveAll } = createKvList<PilotProfile>('pilots.v1');

function emptyPilot(): PilotProfile {
  const now = Date.now();
  return {
    id: newClientId('pi'),
    isDefault: false,
    name: '',
    ratings: {},
    endorsements: {},
    createdAt: now,
    updatedAt: now,
  };
}

function deriveInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0]!)
    .join('')
    .slice(0, 3)
    .toUpperCase();
}

// --- Server <-> local row mappers -----------------------------------

interface PilotRowDb {
  id: string;
  owner_id: string;
  client_id: string | null;
  version: number;
  is_default: boolean;
  name: string;
  initials: string | null;
  certificate_number: string | null;
  certificate_level: string | null;
  ratings: Record<string, boolean>;
  endorsements: Record<string, boolean>;
  medical_class: string | null;
  medical_expires_on: string | null;
  flight_review_on: string | null;
  ipc_on: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

function toDbRow(p: PilotProfile, ownerId: string): {
  owner_id: string;
  client_id: string;
  is_default: boolean;
  name: string;
  initials: string | null;
  certificate_number: string | null;
  certificate_level: string | null;
  ratings: Record<string, boolean>;
  endorsements: Record<string, boolean>;
  medical_class: string | null;
  medical_expires_on: string | null;
  flight_review_on: string | null;
  ipc_on: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
} {
  return {
    owner_id: ownerId,
    client_id: p.id,
    is_default: p.isDefault,
    name: p.name ?? '',
    initials: p.initials ?? null,
    certificate_number: p.certificateNumber ?? null,
    certificate_level: (p.certificateLevel ?? null) as string | null,
    ratings: p.ratings ?? {},
    endorsements: p.endorsements ?? {},
    medical_class: (p.medicalClass ?? null) as string | null,
    medical_expires_on: p.medicalExpiresOn ?? null,
    flight_review_on: p.flightReviewOn ?? null,
    ipc_on: p.ipcOn ?? null,
    email: p.email ?? null,
    phone: p.phone ?? null,
    notes: p.notes ?? null,
  };
}

function fromDbRow(row: PilotRowDb): PilotProfile {
  const localId = row.client_id ?? row.id;
  return {
    id: localId,
    isDefault: row.is_default,
    name: row.name ?? '',
    initials: row.initials ?? undefined,
    certificateNumber: row.certificate_number ?? undefined,
    certificateLevel: (row.certificate_level ?? undefined) as PilotCertificateLevel | undefined,
    ratings: row.ratings ?? {},
    endorsements: row.endorsements ?? {},
    medicalClass: (row.medical_class ?? undefined) as PilotMedicalClass | undefined,
    medicalExpiresOn: row.medical_expires_on ?? undefined,
    flightReviewOn: row.flight_review_on ?? undefined,
    ipcOn: row.ipc_on ?? undefined,
    email: row.email ?? undefined,
    phone: row.phone ?? undefined,
    notes: row.notes ?? undefined,
    createdAt: row.created_at ? Date.parse(row.created_at) : Date.now(),
    updatedAt: row.updated_at ? Date.parse(row.updated_at) : Date.now(),
  };
}

// --- Store ----------------------------------------------------------

interface PilotState {
  list: PilotProfile[];
  hydrated: boolean;
  lastSyncAt: number | null;
  syncing: boolean;
  syncError: string | null;

  hydrate: () => Promise<void>;

  add: (input?: Partial<PilotProfile>) => Promise<PilotProfile>;
  update: (id: string, patch: Partial<PilotProfile>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  setDefault: (id: string) => Promise<void>;

  /** Pull authoritative state from Supabase. Pushes any local rows
   *  first so a fresh sign-in doesn't lose data. Idempotent. */
  syncFromServer: () => Promise<void>;
  /** Push the entire local list to Supabase. Used when setDefault
   *  flips a row's flag and the partial-unique-index constraint
   *  needs the previous default cleared in the same statement. */
  syncToServer: () => Promise<void>;

  defaultPilot: () => PilotProfile | null;
  byId: (id: string | null | undefined) => PilotProfile | null;
}

async function pushOne(
  pilot: PilotProfile,
  set: (s: Partial<PilotState>) => void,
): Promise<void> {
  if (!supabase) return;
  const user = useAuthStore.getState().user;
  if (!user) return;
  try {
    const row = toDbRow(pilot, user.id);
    const { error } = await supabase
      .from('pilots')
      .upsert(row, { onConflict: 'owner_id,client_id' });
    if (error) set({ syncError: error.message });
    else set({ syncError: null, lastSyncAt: Date.now() });
  } catch (e: any) {
    set({ syncError: e?.message ?? String(e) });
  }
}

async function deleteOne(
  clientId: string,
  set: (s: Partial<PilotState>) => void,
): Promise<void> {
  if (!supabase) return;
  const user = useAuthStore.getState().user;
  if (!user) return;
  try {
    const { error } = await supabase
      .from('pilots')
      .delete()
      .eq('owner_id', user.id)
      .eq('client_id', clientId);
    if (error) set({ syncError: error.message });
    else set({ syncError: null, lastSyncAt: Date.now() });
  } catch (e: any) {
    set({ syncError: e?.message ?? String(e) });
  }
}

export const usePilotStore = create<PilotState>((set, get) => ({
  list: [],
  hydrated: false,
  lastSyncAt: null,
  syncing: false,
  syncError: null,

  hydrate: async () => {
    if (get().hydrated) return;
    const list = await loadAll();
    set({ list, hydrated: true });
    // If a session already exists at hydrate-time, sync.
    if (useAuthStore.getState().session) {
      void get().syncFromServer();
    }
  },

  syncFromServer: async () => {
    if (!supabase) return;
    const user = useAuthStore.getState().user;
    if (!user) return;
    set({ syncing: true });
    try {
      const local = get().list;
      if (local.length > 0) {
        const rows = local.map((p) => toDbRow(p, user.id));
        const { error: pushErr } = await supabase
          .from('pilots')
          .upsert(rows, { onConflict: 'owner_id,client_id' });
        if (pushErr) {
          set({ syncing: false, syncError: pushErr.message });
          return;
        }
      }
      const { data, error } = await supabase
        .from('pilots')
        .select('*')
        .eq('owner_id', user.id);
      if (error) {
        set({ syncing: false, syncError: error.message });
        return;
      }
      const fromServer = (data as PilotRowDb[]).map(fromDbRow);
      await saveAll(fromServer);
      set({
        list: fromServer,
        syncing: false,
        syncError: null,
        lastSyncAt: Date.now(),
      });
    } catch (e: any) {
      set({ syncing: false, syncError: e?.message ?? String(e) });
    }
  },

  syncToServer: async () => {
    if (!supabase) return;
    const user = useAuthStore.getState().user;
    if (!user) return;
    set({ syncing: true });
    try {
      const rows = get().list.map((p) => toDbRow(p, user.id));
      const { error } = await supabase
        .from('pilots')
        .upsert(rows, { onConflict: 'owner_id,client_id' });
      set({
        syncing: false,
        syncError: error?.message ?? null,
        lastSyncAt: error ? get().lastSyncAt : Date.now(),
      });
    } catch (e: any) {
      set({ syncing: false, syncError: e?.message ?? String(e) });
    }
  },

  add: async (input) => {
    const base = emptyPilot();
    const next: PilotProfile = {
      ...base,
      ...(input ?? {}),
      id: base.id,
      createdAt: base.createdAt,
      updatedAt: Date.now(),
    };
    const cur = get().list;
    if (cur.length === 0) next.isDefault = true;
    if (!next.initials) next.initials = deriveInitials(next.name || '');
    const list = [...cur, next];
    await saveAll(list);
    set({ list });
    void pushOne(next, (s) => set(s));
    return next;
  },

  update: async (id, patch) => {
    const list = get().list.map((p) => {
      if (p.id !== id) return p;
      const merged = { ...p, ...patch, id: p.id, updatedAt: Date.now() };
      if (patch.name != null && !patch.initials) {
        merged.initials = deriveInitials(merged.name || '');
      }
      return merged;
    });
    await saveAll(list);
    set({ list });
    const updated = list.find((p) => p.id === id);
    if (updated) void pushOne(updated, (s) => set(s));
  },

  remove: async (id) => {
    let list = get().list.filter((p) => p.id !== id);
    if (!list.some((p) => p.isDefault) && list.length > 0) {
      list = list.map((p, i) => ({ ...p, isDefault: i === 0 }));
    }
    await saveAll(list);
    set({ list });
    void deleteOne(id, (s) => set(s));
    const newDefault = list.find((p) => p.isDefault);
    if (newDefault) void pushOne(newDefault, (s) => set(s));
  },

  setDefault: async (id) => {
    const list = get().list.map((p) => ({
      ...p,
      isDefault: p.id === id,
      updatedAt: p.id === id ? Date.now() : p.updatedAt,
    }));
    await saveAll(list);
    set({ list });
    // Push the whole list — the partial unique index on the server
    // needs the previous default cleared in the same statement.
    void get().syncToServer();
  },

  defaultPilot: () => {
    const list = get().list;
    return list.find((p) => p.isDefault) ?? list[0] ?? null;
  },

  byId: (id) => {
    if (!id) return null;
    return get().list.find((p) => p.id === id) ?? null;
  },
}));

// Subscribe to auth changes: when a session appears, kick a server
// sync. When it disappears, leave local data alone (offline mode).
useAuthStore.subscribe((state, prev) => {
  const wasSignedIn = !!prev.user;
  const isSignedIn = !!state.user;
  if (!wasSignedIn && isSignedIn) {
    // Just signed in. If the pilot store is already hydrated, sync;
    // otherwise hydrate() will pick it up via the same session check.
    if (usePilotStore.getState().hydrated) {
      void usePilotStore.getState().syncFromServer();
    }
  }
});
